import { useCallback, useEffect, useState } from 'react';
import { FileCheck2, Plus, RefreshCw, Search, Play, Check, Trash2, Ban, Pencil, Loader2 } from 'lucide-react';
import {
  listContracts,
  createContract,
  updateContract,
  deleteContract,
  publishContract,
  deprecateContract,
  evaluateContract,
  listAgentOptions,
  listEvalSuiteOptions,
  type CapabilityContract,
  type ContractPayload,
  type ContractEvalResult,
  type ContractStatus,
  type ContractVisibility,
  type AgentOption,
  type EvalSuiteOption,
} from '../api/contracts';
import JsonEditor from '../components/JsonEditor';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

const STATUS_STYLES: Record<ContractStatus, { text: string; color: string }> = {
  draft: { text: '草稿', color: 'var(--text-secondary, #8b949e)' },
  published: { text: '已发布', color: 'var(--color-success, #3fb950)' },
  deprecated: { text: '已废止', color: 'var(--color-warning, #d29922)' },
};

const VISIBILITY_STYLES: Record<ContractVisibility, { text: string; color: string }> = {
  private: { text: '私有', color: 'var(--text-secondary, #8b949e)' },
  public: { text: '公开', color: 'var(--color-primary, #58a6ff)' },
};

function Badge({ color, text }: { color: string; text: string }) {
  return (
    <span
      style={{
        fontSize: 12,
        padding: '2px 8px',
        borderRadius: 10,
        border: `1px solid ${color}`,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

/** 五要素摘要：展示 input/output 字段数与规则/护栏/评测标准是否就绪。 */
function EssentialsSummary({ contract }: { contract: CapabilityContract }) {
  const inputFields = Array.isArray(contract.input_domain?.fields) ? contract.input_domain.fields.length : 0;
  const outputFields = Array.isArray(contract.output_domain?.fields) ? contract.output_domain.fields.length : 0;
  const rules = contract.domain_rules?.length ?? 0;
  const guardrails = (contract.guardrails && Object.keys(contract.guardrails).length > 0) ? '✓' : '✗';
  const suiteId = (contract.eval_criteria as { eval_suite_id?: string } | null)?.eval_suite_id ?? '—';
  return (
    <div style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <span>输入字段 {inputFields}</span>
      <span>输出字段 {outputFields}</span>
      <span>领域规则 {rules} 条</span>
      <span>护栏 {guardrails}</span>
      <span>评测套件 {suiteId}</span>
    </div>
  );
}

/** 达标徽标：对照 eval_criteria 阈值判定（未声明阈值不展示判定）。 */
function MetBadge({ result }: { result: ContractEvalResult }) {
  if (result.met === null) {
    return <Badge color="var(--text-secondary, #8b949e)" text="未声明阈值" />;
  }
  return result.met ? <Badge color="var(--color-success, #3fb950)" text="达标" /> : <Badge color="var(--color-error, #f85149)" text="未达标" />;
}

function EvalResultPanel({ result }: { result: ContractEvalResult }) {
  return (
    <div className="card" style={{ background: 'var(--bg-primary)', marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-2">
          <Play size={16} style={{ color: 'var(--color-primary, #58a6ff)' }} />
          <strong>评测结果（{result.suite_id}）</strong>
          <MetBadge result={result} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>run_id: {result.run_id}</span>
      </div>
      <div className="flex items-center gap-4" style={{ marginTop: 8, fontSize: 13, flexWrap: 'wrap' }}>
        <span>
          通过率：<strong>{(result.pass_rate * 100).toFixed(1)}%</strong>（{result.passed}/{result.total}）
        </span>
        <span>综合分：<strong>{result.composite_score.toFixed(2)}</strong></span>
        {result.min_pass_rate !== null && (
          <span className="text-muted">阈值 pass_rate ≥ {(result.min_pass_rate * 100).toFixed(1)}%</span>
        )}
        {result.min_composite_score !== null && (
          <span className="text-muted">阈值 composite ≥ {result.min_composite_score.toFixed(2)}</span>
        )}
      </div>
      {result.failure_summary ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--bg-secondary, #161b22)',
            color: 'var(--text-secondary, #8b949e)',
            whiteSpace: 'pre-wrap',
            maxHeight: 160,
            overflow: 'auto',
          }}
        >
          {result.failure_summary}
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-success, #3fb950)' }}>
          全部用例通过，无失败摘要。
        </div>
      )}
    </div>
  );
}

interface ContractFormProps {
  initial: CapabilityContract | null;
  agents: AgentOption[];
  suites: EvalSuiteOption[];
  busy: boolean;
  onSubmit: (payload: ContractPayload, bumpVersion: boolean) => Promise<void>;
  onCancel: () => void;
}

function ContractForm({ initial, agents, suites, busy, onSubmit, onCancel }: ContractFormProps) {
  const editingPublished = initial?.status === 'published';
  const [bumpVersion, setBumpVersion] = useState(false);
  const frozen = editingPublished && !bumpVersion; // published 冻结：契约字段不可改，除非升版本修订

  const [name, setName] = useState(initial?.name ?? '');
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [inputDomain, setInputDomain] = useState<Record<string, unknown>>(initial?.input_domain ?? {});
  const [outputDomain, setOutputDomain] = useState<Record<string, unknown>>(initial?.output_domain ?? {});
  const [domainRules, setDomainRules] = useState<unknown[]>(initial?.domain_rules ?? []);
  const [guardrails, setGuardrails] = useState<Record<string, unknown>>(initial?.guardrails ?? {});
  const [evalCriteria, setEvalCriteria] = useState<Record<string, unknown>>(initial?.eval_criteria ?? {});
  const [agentId, setAgentId] = useState(initial?.agent_id ?? '');
  const [visibility, setVisibility] = useState<ContractVisibility>(initial?.visibility ?? 'private');
  const [suiteId, setSuiteId] = useState(
    (initial?.eval_criteria as { eval_suite_id?: string } | null)?.eval_suite_id ?? ''
  );
  const [minPassRate, setMinPassRate] = useState(
    (initial?.eval_criteria as { min_pass_rate?: number } | null)?.min_pass_rate?.toString() ?? ''
  );
  const [minComposite, setMinComposite] = useState(
    (initial?.eval_criteria as { min_composite_score?: number } | null)?.min_composite_score?.toString() ?? ''
  );

  const evalCriterionDisabled = editingPublished && !bumpVersion && initial !== null;

  const buildPayload = (): ContractPayload => {
    const criteria: Record<string, unknown> = { ...evalCriteria };
    if (suiteId) criteria.eval_suite_id = suiteId;
    else delete criteria.eval_suite_id;
    if (minPassRate.trim() !== '') criteria.min_pass_rate = Number(minPassRate);
    else delete criteria.min_pass_rate;
    if (minComposite.trim() !== '') criteria.min_composite_score = Number(minComposite);
    else delete criteria.min_composite_score;
    return {
      name,
      display_name: displayName || undefined,
      description: description || undefined,
      input_domain: inputDomain,
      output_domain: outputDomain,
      domain_rules: domainRules,
      guardrails: guardrails,
      eval_criteria: criteria,
      agent_id: agentId || null,
      visibility,
    };
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit(buildPayload(), bumpVersion);
  };

  const disabledStyle = { opacity: 0.6, pointerEvents: 'none' as const };

  return (
    <div className="card" style={{ background: 'var(--bg-primary)', marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <strong>{initial ? `编辑契约「${initial.name}」v${initial.version}` : '新建能力契约'}</strong>
        {editingPublished && (
          <label className="flex items-center gap-2" style={{ fontSize: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={bumpVersion}
              onChange={(e) => setBumpVersion(e.target.checked)}
            />
            升版本修订（version+1，需重新过五要素非空校验）
          </label>
        )}
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label className="block text-sm mb-1">契约 slug（同租户唯一）</label>
          <input
            type="text"
            aria-label="契约 slug"
            placeholder="如 lead-scoring"
            value={name}
            disabled={frozen || !!initial}
            onChange={(e) => setName(e.target.value)}
            style={frozen || !!initial ? disabledStyle : undefined}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">显示名称</label>
          <input
            type="text"
            aria-label="显示名称"
            placeholder="如 线索评分"
            value={displayName}
            disabled={frozen}
            onChange={(e) => setDisplayName(e.target.value)}
            style={frozen ? disabledStyle : undefined}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">描述</label>
        <textarea
          aria-label="描述"
          rows={2}
          value={description}
          disabled={frozen}
          onChange={(e) => setDescription(e.target.value)}
          style={frozen ? disabledStyle : undefined}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">输入域（input_domain：接受的输入 schema / 字段说明）</label>
        <div style={frozen ? disabledStyle : undefined}>
          <JsonEditor value={inputDomain} onChange={(v) => setInputDomain(v as Record<string, unknown>)} rows={4} placeholder='{"fields": [{"name": "lead", "type": "object", "required": true}]}' />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">输出域（output_domain：输出结构 / 格式约定）</label>
        <div style={frozen ? disabledStyle : undefined}>
          <JsonEditor value={outputDomain} onChange={(v) => setOutputDomain(v as Record<string, unknown>)} rows={4} placeholder='{"fields": [{"name": "score", "type": "float"}]}' />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">领域规则（domain_rules 清单）</label>
        <div style={frozen ? disabledStyle : undefined}>
          <JsonEditor value={domainRules as unknown as object} onChange={(v) => setDomainRules(v as unknown[])} rows={3} placeholder='[{"name": "r1", "rule": "评分区间 0-100"}]' />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">护栏（guardrails：budget_cap / rate_limit / sandbox / compliance_scan）</label>
        <div style={frozen ? disabledStyle : undefined}>
          <JsonEditor value={guardrails} onChange={(v) => setGuardrails(v as Record<string, unknown>)} rows={3} placeholder='{"budget_cap": {"enabled": true, "monthly_cost_cap": 100}}' />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="block text-sm mb-1">评测标准（eval_criteria：评测套件 + 达标阈值）</label>
        <div className="flex items-center gap-2" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
          <select
            aria-label="评测套件"
            value={suiteId}
            disabled={evalCriterionDisabled}
            onChange={(e) => setSuiteId(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          >
            <option value="">选择评测套件…</option>
            {suites.map((s) => (
              <option key={s.suite_id} value={s.suite_id}>
                {s.name}（{s.suite_id} · {s.case_count} 用例）
              </option>
            ))}
          </select>
          <input
            type="number"
            aria-label="达标通过率阈值"
            placeholder="min_pass_rate 0-1"
            min={0}
            max={1}
            step={0.05}
            value={minPassRate}
            disabled={evalCriterionDisabled}
            onChange={(e) => setMinPassRate(e.target.value)}
            style={{ width: 130 }}
          />
          <input
            type="number"
            aria-label="达标综合分阈值"
            placeholder="min_composite 0-1"
            min={0}
            max={1}
            step={0.05}
            value={minComposite}
            disabled={evalCriterionDisabled}
            onChange={(e) => setMinComposite(e.target.value)}
            style={{ width: 130 }}
          />
        </div>
        <div style={evalCriterionDisabled ? disabledStyle : undefined}>
          <JsonEditor value={evalCriteria} onChange={(v) => setEvalCriteria(v as Record<string, unknown>)} rows={3} placeholder='{"eval_suite_id": "gaia-lite", "min_pass_rate": 0.6}' />
        </div>
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label className="block text-sm mb-1">绑定实现 Agent（G 评测挂钩对象）</label>
          <select
            aria-label="绑定 Agent"
            value={agentId}
            disabled={frozen}
            onChange={(e) => setAgentId(e.target.value)}
            style={frozen ? disabledStyle : undefined}
          >
            <option value="">不绑定</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">可见性（public = 跨租户 / 跨 X 复用）</label>
          <select
            aria-label="可见性"
            value={visibility}
            disabled={frozen}
            onChange={(e) => setVisibility(e.target.value as ContractVisibility)}
            style={frozen ? disabledStyle : undefined}
          >
            <option value="private">private（仅归属租户可见）</option>
            <option value="public">public（全部租户可发现复用）</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button className="primary" onClick={() => void handleSubmit()} disabled={busy || !name.trim()}>
          {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
          {initial ? (frozen ? '保存描述' : '保存契约') : '创建契约'}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          取消
        </button>
      </div>
    </div>
  );
}

function ContractCard({
  contract,
  busy,
  onEdit,
  onPublish,
  onDeprecate,
  onDelete,
  onEvaluate,
}: {
  contract: CapabilityContract;
  busy: boolean;
  onEdit: (c: CapabilityContract) => void;
  onPublish: (c: CapabilityContract) => void;
  onDeprecate: (c: CapabilityContract) => void;
  onDelete: (c: CapabilityContract) => void;
  onEvaluate: (c: CapabilityContract) => void;
}) {
  const status = STATUS_STYLES[contract.status] ?? { text: contract.status, color: 'var(--text-secondary)' };
  const vis = VISIBILITY_STYLES[contract.visibility] ?? { text: contract.visibility, color: 'var(--text-secondary)' };
  const isDraft = contract.status === 'draft';
  const isPublished = contract.status === 'published';

  return (
    <div className="card" style={{ background: 'var(--bg-primary)', marginBottom: 10 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <strong>{contract.display_name || contract.name}</strong>
          <Badge color={status.color} text={status.text} />
          <Badge color={vis.color} text={vis.text} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>v{contract.version}</span>
          {contract.agent_name && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
              Agent：<strong>{contract.agent_name}</strong>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          {(isDraft || isPublished) && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onEdit(contract)}>
              <Pencil size={14} /> 编辑
            </button>
          )}
          {isDraft && (
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => onPublish(contract)}>
              <Check size={14} /> 发布
            </button>
          )}
          {isPublished && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onEvaluate(contract)}>
              <Play size={14} /> 运行评测
            </button>
          )}
          {isPublished && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onDeprecate(contract)} style={{ color: 'var(--color-warning)', borderColor: 'var(--color-warning)' }}>
              <Ban size={14} /> 废止
            </button>
          )}
          {!isPublished && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onDelete(contract)} style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}>
              <Trash2 size={14} /> 删除
            </button>
          )}
        </div>
      </div>
      {contract.description && (
        <p className="text-muted text-sm" style={{ margin: '8px 0' }}>
          {contract.description}
        </p>
      )}
      <EssentialsSummary contract={contract} />
    </div>
  );
}

export default function Contracts() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [contracts, setContracts] = useState<CapabilityContract[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [suites, setSuites] = useState<EvalSuiteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CapabilityContract | null>(null);
  const [evalResult, setEvalResult] = useState<ContractEvalResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contractsRes] = await Promise.all([
        listContracts({
          status: (statusFilter || undefined) as ContractStatus | undefined,
          visibility: (visibilityFilter || undefined) as ContractVisibility | undefined,
          q: query || undefined,
        }),
      ]);
      setContracts(contractsRes.items);
    } catch (e) {
      toast.error(`加载能力契约失败：${getApiErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  }, [query, statusFilter, visibilityFilter, toast]);

  // 挂载时并行拉取基础数据（内联 promise 链避免 effect 内同步 setState）
  useEffect(() => {
    Promise.all([listContracts(), listAgentOptions(), listEvalSuiteOptions()])
      .then(([res, agentList, suiteList]) => {
        setContracts(res.items);
        setAgents(agentList);
        setSuites(suiteList);
      })
      .catch((e) => {
        toast.error(`加载能力契约失败：${getApiErrorMessage(e)}`);
      })
      .finally(() => setLoading(false));
    // 仅在挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
    setEvalResult(null);
  };

  const openEdit = (c: CapabilityContract) => {
    setEditing(c);
    setFormOpen(true);
  };

  const handleSubmit = async (payload: ContractPayload, bumpVersion: boolean) => {
    setBusy(true);
    try {
      if (editing) {
        await updateContract(editing.id, { ...payload, bump_version: bumpVersion });
        toast.success(bumpVersion ? `契约「${payload.name}」已升版本修订` : `契约「${payload.name}」已更新`);
      } else {
        await createContract(payload);
        toast.success(`能力契约「${payload.name}」已注册（draft）`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(`保存失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async (c: CapabilityContract) => {
    setBusy(true);
    try {
      await publishContract(c.id);
      toast.success(`契约「${c.name}」已发布`);
      await load();
    } catch (e) {
      toast.error(`发布失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDeprecate = async (c: CapabilityContract) => {
    setBusy(true);
    try {
      await deprecateContract(c.id);
      toast.success(`契约「${c.name}」已废止`);
      await load();
    } catch (e) {
      toast.error(`废止失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (c: CapabilityContract) => {
    if (
      !(await confirm({
        title: `确定删除契约「${c.name}」？`,
        message: 'published 契约需先废止。',
        danger: true,
        requireText: c.name,
      }))
    )
      return;
    setBusy(true);
    try {
      await deleteContract(c.id);
      toast.success(`契约「${c.name}」已删除`);
      await load();
    } catch (e) {
      toast.error(`删除失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleEvaluate = async (c: CapabilityContract) => {
    setBusy(true);
    try {
      const result = await evaluateContract(c.id);
      setEvalResult(result);
      toast.success(result.met === null ? '评测完成（未声明阈值，仅展示跑分）' : result.met ? '评测完成，达标' : '评测完成，未达标');
    } catch (e) {
      toast.error(`评测失败：${getApiErrorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const draftCount = contracts.filter((c) => c.status === 'draft').length;

  return (
    <div className="container">
      <h1>
        <FileCheck2 size={22} className="inline mr-1" /> 能力契约
      </h1>
      <p className="text-muted mb-4">
        X→A 能力契约注册表：X（契约作者）注册 {`{输入域, 输出域, 领域规则, 护栏, 评测标准}`}，A 中的 Agent 绑定实现，
        G 按契约评测标准挂钩验证。契约通用可复用（contract not coupling），public 契约跨租户 / 跨 X 可发现。
      </p>

      {evalResult && <EvalResultPanel result={evalResult} />}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <input
            type="text"
            aria-label="契约搜索关键词"
            placeholder="按名称 / 描述搜索"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') load();
            }}
            style={{ flex: 1, minWidth: 220 }}
          />
          <select aria-label="状态筛选" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">全部状态</option>
            <option value="draft">草稿</option>
            <option value="published">已发布</option>
            <option value="deprecated">已废止</option>
          </select>
          <select aria-label="可见性筛选" value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)}>
            <option value="">全部可见性</option>
            <option value="private">私有</option>
            <option value="public">公开</option>
          </select>
          <button className="secondary" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 size={14} className="inline animate-spin mr-1" /> : <Search size={14} className="inline mr-1" />}
            搜索
          </button>
          <button className="primary" onClick={openCreate}>
            <Plus size={14} className="inline mr-1" /> 新建契约
          </button>
        </div>
      </div>

      {formOpen && (
        <ContractForm
          initial={editing}
          agents={agents}
          suites={suites}
          busy={busy}
          onSubmit={handleSubmit}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}

      <div className="flex items-center gap-2 mb-2">
        <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
          共 {contracts.length} 条契约{draftCount > 0 ? ` · ${draftCount} 条草稿待发布` : ''}
        </span>
        <button className="icon-btn" disabled={loading} onClick={() => void load()} title="刷新">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading && contracts.length === 0 ? (
        <div className="loading">
          <Loader2 size={20} className="animate-spin" style={{ marginRight: 8 }} /> 加载中...
        </div>
      ) : contracts.length === 0 ? (
        <div className="card" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary, #8b949e)' }}>
          暂无能力契约。点击「新建契约」注册第一条（draft 起步，补齐五要素后可发布）。
        </div>
      ) : (
        contracts.map((c) => (
          <ContractCard
            key={c.id}
            contract={c}
            busy={busy}
            onEdit={openEdit}
            onPublish={handlePublish}
            onDeprecate={handleDeprecate}
            onDelete={handleDelete}
            onEvaluate={handleEvaluate}
          />
        ))
      )}
      {dialog}
    </div>
  );
}
